<script setup lang="ts">
  import { type RuleExpression, useField } from 'vee-validate'
  import { ref, computed, type PropType, watch, onMounted, onUnmounted } from 'vue'
  import { ExclamationCircleIcon, ChevronUpDownIcon } from '@heroicons/vue/24/outline'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      selectOption: 'Select {label}...',
      search: 'Search {label}...',
      unselectedOption: 'None'
    },
    de: {
      selectOption: '{label} auswählen...',
      search: '{label} suchen...',
      unselectedOption: 'Keine'
    }
  })

  export interface Option {
    value: string | number | null
    title: string
    icon?: Vue.Component
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: [String, Number, null],
      required: false
    },
    rules: {
      type: Object as PropType<RuleExpression<any> | undefined>,
      required: false
    },
    isFilterable: {
      type: Boolean,
      required: false,
      default: false
    },
    options: {
      type: Array as PropType<Option[]>,
      required: true
    },
    inputAction: {
      type: Function,
      required: false,
      default: (_newVal: string, _oldVal: string) => {},
      validator: (fn) => {
        return typeof fn === 'function' && fn.length === 2
      }
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    selectOptionText: {
      type: String,
      required: false
    },
    allowUnselect: {
      type: Boolean,
      required: false,
      default: false
    },
    unselectedOptionText: {
      type: String,
      required: false
    },
    error: {
      type: String,
      required: false
    }
  })

  const emit = defineEmits(['update:modelValue', 'blur', 'focus'])

  const {
    value: model,
    errorMessage: internalErrorMessage,
    handleBlur,
    meta
  } = useField(props.name, props.rules, {
    syncVModel: true
  })

  const computedOptions = computed(() => {
    const unselectedOption: Option = {
      value: '',
      title: props.unselectedOptionText || t('unselectedOption')
    }
    return props.allowUnselect ? [unselectedOption, ...props.options] : props.options
  })

  const touched = ref(false)

  const errorMessage = computed(() => {
    if (props.error) {
      return props.error
    }
    return touched.value ? internalErrorMessage.value : ''
  })

  const selectedOption = computed<Option | null>(() => {
    return computedOptions.value.find((option) => option.value === model.value) ?? null
  })

  const opened = ref(false)
  const selectFieldRef = ref(null)

  const filterQuery = ref('')
  const filteredOptions = computed(() => {
    return computedOptions.value.filter((option) =>
      option.title.toLowerCase().includes(filterQuery.value.toLowerCase())
    )
  })

  const handleOptionClick = (option: Option) => {
    model.value = option.value
    opened.value = false
    touched.value = true
    emit('update:modelValue', option.value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (selectFieldRef.value && !selectFieldRef.value.contains(event.target as Node)) {
      opened.value = false
      filterQuery.value = ''
    }
  }

  const handleBlurEvent = (event: FocusEvent) => {
    touched.value = true
    handleBlur(event)
    emit('blur', event)
  }

  const handleFocusEvent = (event: FocusEvent) => {
    touched.value = true
    emit('focus', event)
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  watch(filterQuery, (newVal, oldVal) => {
    props.inputAction(newVal, oldVal)
  })
</script>

<template>
  <div>
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div
      ref="selectFieldRef"
      class="relative"
    >
      <select
        v-model="model"
        class="hidden"
        style="display: none"
        :disabled="disabled"
        @blur="handleBlurEvent"
        @focus="handleFocusEvent"
      >
        <option value="">Choose</option>
        <option
          v-for="option in computedOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.title }}
        </option>
      </select>
      <button
        class="relative flex w-full cursor-pointer items-center text-nowrap rounded-lg border px-4 py-2 pe-9
          text-start text-sm focus:border-transparent focus:outline-none focus:ring-2"
        :class="[
          errorMessage
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 focus:ring-purple-500',
          disabled ? 'cursor-not-allowed bg-[#f9f9f9]' : 'cursor-pointer bg-white'
        ]"
        type="button"
        :disabled="disabled"
        @click="opened = true"
        @blur="handleBlurEvent"
        @focus="handleFocusEvent"
      >
        <slot
          name="button-contents"
          :selected-option="selectedOption"
        >
          <component
            :is="selectedOption?.icon"
            v-if="selectedOption !== null && selectedOption.icon"
            class="mr-2 size-4"
          />
          <span
            class="overflow-hidden"
            :class="{
              'text-gray-800': selectedOption !== null,
              'text-gray-500': selectedOption === null
            }"
          >
            {{
              selectedOption === null
                ? selectOptionText || t('selectOption', { label: label })
                : selectedOption.title
            }}
          </span>
        </slot>
      </button>
      <div
        v-if="errorMessage"
        class="absolute end-8 top-1/2 -translate-y-1/2"
      >
        <ExclamationCircleIcon class="size-5 flex-shrink-0 text-red-500" />
      </div>
      <ChevronUpDownIcon
        class="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600"
      ></ChevronUpDownIcon>
      <div
        v-show="opened"
        class="absolute z-20 mt-2 max-h-72 space-y-0.5 overflow-y-auto rounded-lg border border-gray-200 bg-white
          px-1 pb-1"
        :class="[isFilterable ? '' : 'pt-1', disabled ? 'cursor-not-allowed' : '']"
      >
        <div
          v-if="isFilterable"
          class="sticky top-0 -mx-1 bg-white p-2"
        >
          <input
            v-model="filterQuery"
            class="block w-full rounded-lg border-gray-200 px-3 py-2 text-sm focus:border-purple-500
              focus:ring-purple-500"
            :class="{ 'cursor-not-allowed': disabled }"
            type="text"
            :placeholder="t('search', { label: label })"
            :disabled="disabled"
            @click.stop="opened = true"
          />
        </div>
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          tabindex="0"
          class="w-full cursor-pointer rounded-lg px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100
            focus:outline-none"
          :class="{
            'cursor-not-allowed': disabled,
            'bg-gray-50': option.value === selectedOption?.value
          }"
          @click="handleOptionClick(option)"
        >
          <div class="flex items-center">
            <slot
              name="option-contents"
              :option="option"
            >
              <component
                :is="option.icon"
                v-if="option.icon"
                class="mr-2 size-4"
              />
              <div
                :class="{
                  'text-gray-500': option.value === null,
                  'text-gray-800': option.value !== null
                }"
              >
                {{ option.title }}
              </div>
            </slot>
          </div>
        </div>
      </div>
    </div>
    <p
      v-if="errorMessage"
      class="mt-1 text-xs text-red-600"
      :class="{ 'opacity-50': disabled }"
    >
      {{ errorMessage }}
    </p>
  </div>
</template>
